import React, { useEffect, useCallback } from 'react';
import { FiX } from 'react-icons/fi';
import './UI.css';

/**
 * Componente Modal reutilizável
 * 
 * @param {boolean} isOpen - Controla se o modal está aberto
 * @param {function} onClose - Callback ao fechar o modal
 * @param {string} title - Título do modal
 * @param {string} size - 'sm' | 'md' | 'lg' | 'xl' | 'full'
 * @param {boolean} closeOnOverlayClick - Fechar ao clicar no overlay
 * @param {boolean} closeOnEscape - Fechar ao pressionar Escape
 * @param {boolean} showCloseButton - Mostrar botão de fechar
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  footer,
  className = '',
  ...props
}) => {
  // Fechar com ESC
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && closeOnEscape) {
      onClose?.();
    }
  }, [closeOnEscape, onClose]);

  // Event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  // Não renderiza se não estiver aberto
  if (!isOpen) return null;

  // Fechar ao clicar no overlay
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose?.();
    }
  };

  const modalClasses = [
    'ui-modal',
    `ui-modal--${size}`,
    className
  ].filter(Boolean).join(' ');

  return (
    <div className="ui-modal-overlay" onClick={handleOverlayClick}>
      <div className={modalClasses} role="dialog" aria-modal="true" {...props}>
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="ui-modal__header">
            {title && <h2 className="ui-modal__title">{title}</h2>}
            {showCloseButton && (
              <button
                type="button"
                className="ui-modal__close"
                onClick={onClose}
                aria-label="Fechar modal"
              >
                <FiX />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="ui-modal__body">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="ui-modal__footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * ConfirmModal - Modal de confirmação pré-configurado
 */
export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirmar ação',
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'error',
  loading = false,
  ...props
}) => {
  const variantColors = {
    error: 'ui-button--error',
    warning: 'ui-button--warning',
    success: 'ui-button--success',
    primary: 'ui-button--primary',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <div className="ui-modal__footer-actions">
          <button
            type="button"
            className="ui-button ui-button--ghost"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={`ui-button ${variantColors[variant]}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? <span className="ui-button__spinner" /> : confirmText}
          </button>
        </div>
      }
      {...props}
    >
      <p className="ui-modal__message">{message}</p>
    </Modal>
  );
};

export default Modal;
